// Third-party packages
import {
	ensureOrganizationDataSource,
	readCachedOrganizationDataSource,
} from '@helpers/getOrganizationDataSource';
import {
	isNestedBrowsingContext,
} from '@helpers/isNestedBrowsingContext';
import {
	isOnline, onNetworkStatusChange,
} from '@helpers/networkStatus';

// Internal libraries (@stonal-tech/*)
import {
	getCurrentOrganization,
	type OrganizationDataSource,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import {
	logger,
} from '@config/logger';
import {
	AppDescription, Destination, IAnalytics, TrackOptions,
} from '@local_types/index';

// Relative imports
import {
	GTM_DESTINATION_NAME, ORG_DATA_SOURCE_RESOLVED_EVENT,
} from '../config/destinations';
import {
	MAX_BUFFER_SIZE,
} from '../config/networkStatus';
import {
	MAX_NAVIGATION_DEFER_MS, NAVIGATION_DEBOUNCE_MS, PAGE_VIEW_DEDUP_WINDOW_MS, PAGE_VIEW_IGNORED_QUERY_PARAMS,
} from '../config/pageViews';
import {
	STORAGE_KEY_USER_GROUPS_BY_ORG,
} from '../config/storageKeys';

// Vite injects BUILD_VERSION at build time

// @ts-ignore
declare const BUILD_VERSION: string;

/**
 * Identifies the page a URL describes, for deduplication purposes.
 *
 * One-shot authentication parameters are dropped: an OAuth callback and the
 * cleaned URL the app rewrites right after it are the same page, even though
 * their raw query strings differ on every single cycle. Every other parameter is
 * page state and is kept.
 */
function getPageViewKey(): string {
	const params = new URLSearchParams(window.location.search);
	for (let i = 0; i < PAGE_VIEW_IGNORED_QUERY_PARAMS.length; i++) {
		params.delete(PAGE_VIEW_IGNORED_QUERY_PARAMS[i]);
	}
	const search = params.toString();

	return window.location.pathname + (search ? '?' + search : '') + window.location.hash;
}

/**
 * Get the app version from the page's meta tag
 */
function getAppVersion(): string | undefined {
	const metaVersion = document.querySelector('meta[name="version"]');
	return metaVersion?.getAttribute('content') || undefined;
}

/**
 * Whether a destination receives an event restricted to `destinations` (all do when unset).
 */
function isTargeted(dest: Destination, destinations: string[] | undefined): boolean {
	return !destinations || destinations.includes(dest.name);
}

/**
 * Represents a buffered event stored while offline
 */
type BufferedEvent =
	| { type: 'track';
		event: string;
		properties: Record<string, any>;
		destinations?: string[] }
	| { type: 'page';
		name: string;
		properties: Record<string, any> }
	| { type: 'identify';
		userId: string | undefined;
		traits: Record<string, any> }
	| { type: 'group';
		groupId: string;
		traits: Record<string, any> };

/**
 * Core analytics object
 *
 * This is the main object that allows to track events and identify users.
 * When offline, events are buffered in memory and replayed when connectivity returns.
 *
 */
export class Analytics implements IAnalytics {
	readonly version: string = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'dev';
	private destinations: Destination[] = [];
	private app: AppDescription | undefined = undefined;
	private appVersion: string | undefined = undefined;
	private currentOrg: string | undefined = undefined;
	private userGroupsByOrg: Record<string, string[]> | undefined = undefined;
	private orgDataSource: OrganizationDataSource | undefined = undefined;
	/**
	 * Organization the data source has already been resolved for, so that `setApp`
	 * firing on every navigation resolves at most once per organization — even when
	 * localStorage refused the write and the persisted cache stayed empty.
	 */
	private orgDataSourceResolvedFor: string | undefined = undefined;
	private offlineBuffer: BufferedEvent[] = [];
	private _isOffline: boolean = false;
	private lastPageViewUrl: string | undefined = undefined;
	private lastPageViewAt: number = 0;
	private nestedContextReported: boolean = false;
	private pageViewTimer: ReturnType<typeof setTimeout> | undefined = undefined;
	private pendingPageView: {
		name?: string;
		properties?: Record<string, any>;
	} | undefined = undefined;
	private pageViewDeferredSince: number = 0;
	private pagehideListener: (() => void) | undefined = undefined;
	private suppressedPageViews: number = 0;
	private lastSuppressionGate: 'dedup' | 'debounce' | undefined = undefined;

	constructor() {
		this.appVersion = getAppVersion();
		this._isOffline = !isOnline();
		// Eagerly restore userGroupsByOrg from localStorage cache
		// so that page events fired before identCheck.run() already have groups
		try {
			const cached = localStorage.getItem(STORAGE_KEY_USER_GROUPS_BY_ORG);
			if (cached) {
				this.userGroupsByOrg = JSON.parse(cached);
			}
		} catch { /* ignore */ }
		onNetworkStatusChange((online) => {
			this._isOffline = !online;
			if (online) {
				logger.info('Network restored, flushing offline buffer (' + this.offlineBuffer.length + ' events)');
				this._flushBuffer();
			} else {
				logger.info('Network lost, buffering events');
			}
		});
	}

	setApp(app: AppDescription | undefined) {
		if (app === undefined) {
			return;
		}

		this.app = app;
		this.appVersion = getAppVersion();

		this.currentOrg = getCurrentOrganization();

		// Restore the cached data source here rather than in the constructor: the
		// cache is scoped to an organization, and the organization is only known
		// once the app is set. Restoring it earlier means restoring it org-blind,
		// which re-emits the previous organization's value on every event of the
		// page load after a switch.
		//
		// This is still early enough for GTM, which reads the first page event of
		// the page load: `_extendProperties` injects nothing while `this.app` is
		// unset, and it is set just above, so no event can precede this restore.
		//
		// The assignment is unconditional so that switching organization clears a
		// value that no longer applies. When the cache could not be written the
		// trait goes missing until identification resolves it again — recoverable,
		// where a value carried over from another organization would not be.
		this.orgDataSource = readCachedOrganizationDataSource(this.currentOrg);

		// Resolving it is this method's job rather than IdentCheck's: identification
		// takes the OIDC branch only on a session that has never confirmed one, and
		// `stn_tr_id_confirmed` never expires — so hanging the resolution off that
		// branch meant it never ran again for anyone who had already identified.
		//
		// Fire-and-forget, because `setApp` is synchronous and is called before the
		// first page event. A first visit to an organization therefore emits that
		// first event without the property; every later load reads the cache. That
		// gap is what `_announceOrganizationDataSource` closes, rather than holding
		// the page view back — waiting would delay every app in the fleet, and would
		// still leave Beamer on the previous value after an organization switch made
		// without a full page load.
		if (this.currentOrg && this.orgDataSourceResolvedFor !== this.currentOrg) {
			const orgToResolve = this.currentOrg;
			this.orgDataSourceResolvedFor = orgToResolve;
			void ensureOrganizationDataSource(app, orgToResolve).then((dataSource) => {
				// Ignore a result that arrived after a navigation changed organization.
				// The comparison has to be against the organization captured above:
				// `this.currentOrg` is reassigned from `getCurrentOrganization()` at the
				// top of every `setApp`, so comparing those two agrees with itself even
				// after a switch, and the value resolved for the previous organization
				// would be stamped on the new one's events.
				if (dataSource && orgToResolve === this.currentOrg && orgToResolve === getCurrentOrganization()) {
					// Announce only a value the events of this page load were not already
					// carrying. A cache hit resolves to exactly what the restore above put
					// there, and announcing it would fire the tag on every single load for
					// a filter that has not moved.
					const isNewValue = dataSource !== this.orgDataSource;
					this.orgDataSource = dataSource;
					if (isNewValue) {
						this._announceOrganizationDataSource();
					}
				}
			});
		}

		const appProps = this._getAppProperties();
		window.stonal_app = appProps.app;
		window.stonal_env = appProps.stonal_env;
		window.stonal_org = this.currentOrg;
		window.stonal_stack = appProps.stonal_stack;
	}

	/**
	 * Announces a freshly resolved organization data source on the `dataLayer`.
	 *
	 * The value is resolved over the network, so on a first visit to an organization
	 * it lands after that page load's page view — the event Google Tag Manager reads
	 * to configure Beamer. Beamer takes its filter once, at load, so without a later
	 * signal it keeps targeting the organization as unknown until the next full page
	 * load. A tag listening for this event can call `Beamer.update()` with the filter
	 * the value completes.
	 *
	 * Sent to Google Tag Manager alone rather than through `track()`, for two reasons:
	 * it describes no user action, so it does not belong in the product event stream;
	 * and page views already dominate the analytics quota, so a technical event fanned
	 * out to every destination would be paid for on every first visit.
	 *
	 * Carries the same properties as any other event, so a tag can rebuild the whole
	 * filter from this one push rather than depending on values a previous push left
	 * behind. Never throws: the destination swallows its own errors, and a missing
	 * destination is the ordinary case for an app that loads no tag manager.
	 */
	private _announceOrganizationDataSource() {
		const gtm = this.destinations.find((dest) => dest.name === GTM_DESTINATION_NAME);
		gtm?.track?.(ORG_DATA_SOURCE_RESOLVED_EVENT, this._extendProperties({}));
	}

	/**
	 * Reports a navigation as a page view once the burst of history writes it
	 * produced has settled (`NAVIGATION_DEBOUNCE_MS`). URL state — filters, sort,
	 * pagination, search — lands as several successive `replaceState` calls, and
	 * only the URL they settle on is a page view.
	 *
	 * Only the page view is deferred. Navigation detection stays synchronous, so
	 * the app and organization republished on navigation are already current for
	 * every other event; deferring that too would stamp them with the previous
	 * app for the length of the window.
	 *
	 * The wait is capped at `MAX_NAVIGATION_DEFER_MS`, because a burst that never
	 * settles — a search box writing history on every keystroke — must still be
	 * reported rather than deferred forever. A pending view is also flushed on
	 * `pagehide`, otherwise leaving the page inside the window would lose it.
	 */
	schedulePageView (name?: string, properties?: Record<string, any>) {
		const now = Date.now();

		if (this.pageViewTimer === undefined) {
			this.pageViewDeferredSince = now;
		} else {
			// A view was already pending for this burst: it is superseded by a more
			// recent URL rather than sent, so it counts as suppressed.
			clearTimeout(this.pageViewTimer);
			this.suppressedPageViews++;
			this.lastSuppressionGate = 'debounce';
		}

		this.pendingPageView = {
			name,
			properties,
		};
		this._addPagehideListener();

		const remaining = MAX_NAVIGATION_DEFER_MS - (now - this.pageViewDeferredSince);
		const delay = Math.max(0, Math.min(NAVIGATION_DEBOUNCE_MS, remaining));

		this.pageViewTimer = setTimeout(() => this._flushPendingPageView(), delay);
	}

	/**
	 * Sends the page view waiting on the debounce, if any, and clears the timer,
	 * the pending state and the `pagehide` listener that guarded it.
	 */
	private _flushPendingPageView() {
		if (this.pageViewTimer !== undefined) {
			clearTimeout(this.pageViewTimer);
			this.pageViewTimer = undefined;
		}
		this._removePagehideListener();
		this.pageViewDeferredSince = 0;

		const pending = this.pendingPageView;
		this.pendingPageView = undefined;

		if (pending) {
			this.page(pending.name, pending.properties);
		}
	}

	private _addPagehideListener() {
		if (this.pagehideListener !== undefined) {
			return;
		}
		this.pagehideListener = () => this._flushPendingPageView();
		window.addEventListener('pagehide', this.pagehideListener);
	}

	private _removePagehideListener() {
		if (this.pagehideListener === undefined) {
			return;
		}
		window.removeEventListener('pagehide', this.pagehideListener);
		this.pagehideListener = undefined;
	}

	/**
	 * Cancels a page view still waiting on the debounce. Exposed for teardown, so
	 * a caller that stops listening to navigation does not leave a timer behind.
	 */
	cancelPendingPageView () {
		if (this.pageViewTimer !== undefined) {
			clearTimeout(this.pageViewTimer);
			this.pageViewTimer = undefined;
		}
		this._removePagehideListener();
		this.pendingPageView = undefined;
		this.pageViewDeferredSince = 0;
	}

	/**
	 * Decides whether a page view describes something real.
	 *
	 * Two cases are dropped:
	 *
	 * - **nested browsing context**: the application booted inside a frame, so
	 *   nobody navigated and there is no page view to report.
	 * - **the URL we just reported**: `page()` is called on every history entry
	 *   change, and a single navigation can rewrite its URL several times
	 *   (query-param state, a canonical redirect). Only the first counts. The
	 *   comparison ignores one-shot auth parameters (`getPageViewKey()`), so an
	 *   OAuth callback and the cleaned URL that follows it count as one page —
	 *   otherwise a client re-authorizing in a loop reports two page views per
	 *   cycle, each with a query string it has never seen before.
	 *
	 * Deliberately silent on the deduplicated path: this runs on every history
	 * write, and logging there would only move the volume to another pipe. The
	 * drops are counted instead and reported on the next page view that is sent,
	 * so a page-view class that stops being recorded is still visible in the data.
	 */
	private _shouldSendPageView(): boolean {
		if (isNestedBrowsingContext()) {
			if (!this.nestedContextReported) {
				this.nestedContextReported = true;
				logger.info('Page views disabled: document runs in a nested browsing context');
			}
			return false;
		}

		const url = getPageViewKey();
		const now = Date.now();

		if (url === this.lastPageViewUrl && now - this.lastPageViewAt < PAGE_VIEW_DEDUP_WINDOW_MS) {
			this.suppressedPageViews++;
			this.lastSuppressionGate = 'dedup';
			return false;
		}

		this.lastPageViewUrl = url;
		this.lastPageViewAt = now;

		return true;
	}

	page (name?: string, properties?: Record<string, any>) {
		if (!this._shouldSendPageView()) {
			return;
		}
		properties = this._extendProperties(properties || {});
		// Report the page views suppressed since the last one we sent. There is no
		// backfill for an event we did not send, so this counter is the only way to
		// tell afterwards whether the gates dropped noise or real traffic. It costs
		// no extra event: it rides on the next page view that passes. Nothing is
		// reported for a nested context, which would be 1:1 with the volume the
		// gate exists to remove.
		if (this.suppressedPageViews > 0) {
			properties.page_views_suppressed = this.suppressedPageViews;
			properties.page_view_gate = this.lastSuppressionGate;
			this.suppressedPageViews = 0;
			this.lastSuppressionGate = undefined;
		}
		if (this._isOffline) {
			properties._offline = true;
			properties._offline_timestamp = new Date().toISOString();
			this._addToBuffer({
				name: name || '',
				properties: properties || {},
				type: 'page',
			});
			// Resilient destinations receive events even when offline
			this._dispatchToResilient('page', name || '', properties || {});
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.page && dest.page(name || '', properties || {});
		}
	}

	identify (userId: string | undefined, traits: Record<string, any>) {
		// Store userGroups map for lookup at event time in _extendProperties
		if (traits?.userGroups) {
			this.userGroupsByOrg = traits.userGroups;
			// Persist so the constructor can eagerly restore on next page load
			try {
				localStorage.setItem(STORAGE_KEY_USER_GROUPS_BY_ORG, JSON.stringify(traits.userGroups));
			} catch { /* ignore quota errors */ }
		}
		if (this._isOffline) {
			this._addToBuffer({
				traits,
				type: 'identify',
				userId,
			});
			this._dispatchToResilient('identify', userId, traits);
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.identify && dest.identify(userId, traits);
		}
	}


	group (groupId: string, traits?: Record<string, any>) {
		// Associates the current user with a group/organization
		// Useful for B2B tracking and organization-level analysis
		if (this._isOffline) {
			this._addToBuffer({
				groupId,
				traits: traits || {},
				type: 'group',
			});
			this._dispatchToResilient('group', groupId, traits || {});
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.group && dest.group(groupId, traits || {});
		}
	}

	track (event: string, properties?: Record<string, any>, options?: TrackOptions) {
		const destinations = options?.destinations;
		properties = this._extendProperties(properties);
		if (this._isOffline) {
			properties._offline = true;
			properties._offline_timestamp = new Date().toISOString();
			this._addToBuffer({
				destinations,
				event,
				properties,
				type: 'track',
			});
			this._dispatchToResilient('track', event, properties, destinations);
			return;
		}
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			if (!isTargeted(dest, destinations)) continue;
			dest.track && dest.track(event, properties);
		}
	}

	addDestination (dest: Destination) {
		// We check if the destination is already present
		for (let i = 0; i < this.destinations.length; i++) {
			if (this.destinations[i].name === dest.name) {
				logger.error('Destination ' + dest.name + ' already present');
				return;
			}
		}
		this.destinations.push(dest);
	}

	error (event: string, properties: any) {
		logger.error(event + ' : ' + JSON.stringify(properties));
		// Track error event to all destinations with "Error: " prefix
		this.track(`Error: ${event}`, properties);
	}

	private _getAppProperties(): { app: string;
		stonal_env: string | undefined;
		stonal_stack: string | undefined } {
		// This method should only be called when this.app is defined
		// Use tracking_code if available, otherwise fall back to code
		return {
			app: this.app!.tracking_code || this.app!.code,
			stonal_env: this.app!.env,
			stonal_stack: this.app!.stack,
		};
	}

	private _extendProperties (properties: Record<string, any> | undefined): Record<string, any> {
		properties = properties || {};
		if (this.app) {
			const appProps = this._getAppProperties();
			properties.app = appProps.app;
			properties.stonal_env = appProps.stonal_env;
			if (this.currentOrg) {
				properties.current_organization = this.currentOrg;
			}
			// Resolve current user groups at event time using the latest currentOrg
			if (this.userGroupsByOrg && this.currentOrg) {
				properties.current_user_groups = this.userGroupsByOrg[this.currentOrg]
					?? Object.entries(this.userGroupsByOrg)
						.find(([
							key,
						]) => key.toLowerCase() === this.currentOrg!.toLowerCase())?.[1];
			}
			if (this.orgDataSource) {
				properties.org_data_source = this.orgDataSource;
			}
			properties.stonal_stack = appProps.stonal_stack;
		}
		if (this.appVersion) {
			properties.app_version = this.appVersion;
		}
		// Inject the build version from the environment
		properties.stonal_tracking_version = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'dev';
		return properties;
	}

	/**
	 * Dispatches an event to offline-resilient destinations only.
	 * These destinations handle their own queueing/retry (e.g. RudderStack localStorage queue).
	 */
	private _dispatchToResilient(type: 'track', event: string, properties: Record<string, any>, destinations?: string[]): void;
	private _dispatchToResilient(type: 'page', name: string, properties: Record<string, any>): void;
	private _dispatchToResilient(type: 'identify', userId: string | undefined, traits: Record<string, any>): void;
	private _dispatchToResilient(type: 'group', groupId: string, traits: Record<string, any>): void;
	private _dispatchToResilient(type: string, arg1: any, arg2: any, destinations?: string[]): void {
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			if (!dest.offlineResilient || !isTargeted(dest, destinations)) continue;
			switch (type) {
				case 'track':
					dest.track && dest.track(arg1, arg2);
					break;
				case 'page':
					dest.page && dest.page(arg1, arg2);
					break;
				case 'identify':
					dest.identify && dest.identify(arg1, arg2);
					break;
				case 'group':
					dest.group && dest.group(arg1, arg2);
					break;
			}
		}
	}

	/**
	 * Adds an event to the offline buffer, capping at MAX_BUFFER_SIZE
	 */
	private _addToBuffer(event: BufferedEvent): void {
		if (this.offlineBuffer.length >= MAX_BUFFER_SIZE) {
			// Drop the oldest event to make room
			this.offlineBuffer.shift();
		}
		this.offlineBuffer.push(event);
	}

	/**
	 * Flushes the offline buffer to non-resilient destinations only.
	 * Resilient destinations already received events in real-time via _dispatchToResilient.
	 */
	private _flushBuffer(): void {
		const events = [
			...this.offlineBuffer,
		];
		this.offlineBuffer = [];

		for (const bufferedEvent of events) {
			for (let i = 0; i < this.destinations.length; i++) {
				const dest = this.destinations[i];
				if (dest.offlineResilient) continue;
				switch (bufferedEvent.type) {
					case 'track':
						if (!isTargeted(dest, bufferedEvent.destinations)) break;
						dest.track && dest.track(bufferedEvent.event, bufferedEvent.properties);
						break;
					case 'page':
						dest.page && dest.page(bufferedEvent.name, bufferedEvent.properties);
						break;
					case 'identify':
						dest.identify && dest.identify(bufferedEvent.userId, bufferedEvent.traits);
						break;
					case 'group':
						dest.group && dest.group(bufferedEvent.groupId, bufferedEvent.traits);
						break;
				}
			}
		}
	}
}
